import ApiError from '../../api/ApiError';
import type {
  CeDirectorySafeLocation,
  CeDirectorySourceSurface,
} from './types';
import { getCeDirectoryErrorCode } from './errors';

export type CeLocationContextInvalidEvent = {
  reason: 'selection_required' | 'access_denied';
  locations?: CeDirectorySafeLocation[];
};

function mapSafeLocation(value: unknown): CeDirectorySafeLocation | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  if (!Number.isInteger(candidate.id) || Number(candidate.id) < 1 || typeof candidate.label !== 'string') {
    return null;
  }
  const nullableString = (field: unknown) => field === null || typeof field === 'string';
  if (!nullableString(candidate.city)
      || !nullableString(candidate.provinceState)
      || !(candidate.countryCode === null || candidate.countryCode === 'CA' || candidate.countryCode === 'US')) {
    return null;
  }
  return {
    id: Number(candidate.id),
    label: candidate.label,
    city: candidate.city as string | null,
    provinceState: candidate.provinceState as string | null,
    countryCode: candidate.countryCode as 'CA' | 'US' | null,
  };
}

/**
 * Convert only the two explicit office directory error codes into the public
 * wrapper callback. Generic/click 403s intentionally do not enter this path.
 */
export function getLocationContextInvalidEvent(
  error: unknown,
  sourceSurface: CeDirectorySourceSurface,
): CeLocationContextInvalidEvent | null {
  if (sourceSurface !== 'office') return null;
  const code = getCeDirectoryErrorCode(error);
  if (code === 'LOCATION_ACCESS_DENIED') return { reason: 'access_denied' };
  if (code !== 'LOCATION_SELECTION_REQUIRED') return null;

  const locations = ((error as Partial<ApiError>)?.data as any)?.locations;
  if (!Array.isArray(locations)) return { reason: 'selection_required' };
  const safeLocations = locations
    .map(mapSafeLocation)
    .filter((location): location is CeDirectorySafeLocation => location !== null);
  return {
    reason: 'selection_required',
    ...(safeLocations.length > 0 ? { locations: safeLocations } : {}),
  };
}
